import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { adminRequest, fillEndpoint } from "../../services/adminRequest";
import { useOptionsClientId } from "../../hooks/useOptionsClientId";
import { normalizeClientApiKeys } from "../../utils/clientApiKeys";
import { buildKeyPayload } from "../../utils/nodeBuilder";
import { isoToLocalInput, localInputToIso } from "../../utils/datetime";

// Metadata-driven client key management: the catalog's `builder.client_key_form` names the
// endpoints and the fields, so nothing here is Slack-specific.

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000000001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 85vh;
  overflow: auto;
  padding: 16px;
  border-radius: 10px;
  background-color: var(--dialog-bg);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.div`
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  color: var(--main-font-color);
`;

const fieldStyles = `
  width: 100%;
  padding: 6px 9px;
  font-size: 12.5px;
  font-family: inherit;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
`;

const Input = styled.input`${fieldStyles}`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: var(--main-font-color);
`;
const Hint = styled.div`
  font-size: 11px;
  font-style: italic;
  color: #a0a6b1;
`;
const ErrorText = styled.div`
  font-size: 12px;
  color: #e74c3c;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid var(--content-item-border-color);
  font-size: 12.5px;
  color: var(--main-font-color);
`;
const Small = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 11.5px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
`;

const fieldsFor = (form) => (Array.isArray(form?.fields) ? form.fields : []);

/**
 * @param {object} props
 * @param {object} props.form The `builder.client_key_form` metadata.
 * @param {Function} props.onClose
 */
export const ClientKeyFormDialog = ({ form = null, onClose = () => {} }) => {
  const clientId = useOptionsClientId();

  const fields = fieldsFor(form);
  const service = form?.service ?? null;
  const listEndpoint = form?.list_endpoint ?? "/admin/client-api-keys";
  const saveEndpoint = form?.save_endpoint ?? "/admin/client-api-keys";
  const deleteEndpoint = form?.delete_endpoint ?? "/admin/client-api-keys/{name}";

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    const query = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
    const result = await adminRequest("GET", `${listEndpoint}${query}`);
    setLoading(false);

    if (result?.success === false) {
      setKeys([]);
      setError(result.message || "Could not load the keys.");
      return;
    }

    setKeys(normalizeClientApiKeys(result));
    setError(null);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const save = async () => {
    const missing = fields.filter((field) => field?.required && !String(values[field.name] ?? "").trim());
    if (missing.length > 0) {
      setError(`Required: ${missing.map((field) => field.label ?? field.name).join(", ")}`);
      return;
    }

    setSaving(true);
    setError(null);

    const result = await adminRequest("POST", saveEndpoint, buildKeyPayload(fields, values, { service, clientId }));

    setSaving(false);

    if (result?.success === false) {
      setError(result.message || "Could not save the key.");
      return;
    }

    setValues({});
    await load();
    window.dispatchEvent(new CustomEvent("client-api-keys:changed"));
  };

  const remove = async (key) => {
    setConfirmDelete(null);
    const query = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
    const result = await adminRequest(
      "DELETE",
      `${fillEndpoint(deleteEndpoint, { name: key.name })}${query}`
    );

    if (result?.success === false) {
      setError(result.message || "Could not delete the key.");
      return;
    }

    await load();
    window.dispatchEvent(new CustomEvent("client-api-keys:changed"));
  };

  const renderField = (field) => {
    const type = String(field?.type ?? "string").toLowerCase();
    const value = values[field.name] ?? "";

    const onChange = (next) => setValues((current) => ({ ...current, [field.name]: next }));

    return (
      <Field key={field.name}>
        <Label>
          {field.label ?? field.name}
          {field.required ? <span style={{ color: "#e74c3c" }}> *</span> : null}
        </Label>
        {type === "datetime" ? (
          <Input
            type="datetime-local"
            value={isoToLocalInput(value)}
            onChange={(event) => onChange(localInputToIso(event.target.value))}
          />
        ) : (
          <Input
            type={type === "password" ? "password" : "text"}
            value={value}
            autoComplete={type === "password" ? "new-password" : "off"}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
        {type === "datetime" ? <Hint>Sent as ISO-8601 UTC.</Hint> : null}
      </Field>
    );
  };

  return (
    <Overlay>
      <Panel>
        <Header>
          <Icon icon="lucide:key-round" />
          <Title>{service ? `${service} keys` : "Client API keys"}</Title>
          <Small type="button" onClick={onClose}>
            <Icon icon="lucide:x" /> Close
          </Small>
        </Header>

        <Hint>Secrets are write-only and never returned; only a masked value is shown.</Hint>

        {error ? <ErrorText>{error}</ErrorText> : null}

        <div>
          {loading ? <Hint>Loading keys…</Hint> : null}
          {!loading && keys.length === 0 ? <Hint>No keys yet.</Hint> : null}
          {keys.map((key) => (
            <Row key={key.name}>
              <span style={{ fontFamily: "monospace" }}>{key.name}</span>
              <span style={{ flex: 1, color: "#a0a6b1" }}>{key.maskedValue ?? "hidden"}</span>
              {confirmDelete === key.name ? (
                <>
                  <Small type="button" onClick={() => remove(key)}>
                    <Icon icon="lucide:trash-can-outline" /> Confirm
                  </Small>
                  <Small type="button" onClick={() => setConfirmDelete(null)}>
                    <Icon icon="lucide:x" /> Cancel
                  </Small>
                </>
              ) : (
                <Small type="button" onClick={() => setConfirmDelete(key.name)}>
                  <Icon icon="lucide:trash-can-outline" /> Delete
                </Small>
              )}
            </Row>
          ))}
        </div>

        {fields.map(renderField)}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Small type="button" onClick={onClose}>Cancel</Small>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            style={{
              padding: "6px 14px",
              fontSize: "12.5px",
              fontWeight: 600,
              border: "none",
              borderRadius: "6px",
              background: "var(--button-bg)",
              color: "var(--button-font-color)",
              cursor: saving ? "default" : "pointer",
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Saving…" : "Save key"}
          </button>
        </div>
      </Panel>
    </Overlay>
  );
};

export default ClientKeyFormDialog;
